import { useId, useRef, useState, type ReactNode } from 'react';
import { Camera } from 'lucide-react';
import { api } from '../api.js';
import { AVATAR_MAX_BYTES, AVATAR_TYPES } from '../../../../packages/shared/src/avatars.js';
import { Avatar } from './Avatar.js';

/**
 * The owner's identity row with an editable picture: the plate opens the picker and uploads
 * straight away. `children` is the name block the picture controls sit under.
 */
export function ProfilePicture<U extends { avatarUrl: string | null }>({
  user,
  csrfToken,
  onUser,
  children,
}: {
  user: U;
  csrfToken: string;
  onUser: (user: U) => void;
  children: ReactNode;
}) {
  const id = useId(),
    input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [feedback, setFeedback] = useState<{ error: boolean; text: string } | null>(null);
  async function send(method: 'PUT' | 'DELETE', body?: FormData) {
    setBusy(true);
    setFeedback(null);
    try {
      const result = await api<{ user: U; message: string }>('/api/v1/account/avatar', {
        method,
        headers: { 'X-CSRF-Token': csrfToken },
        body,
      });
      onUser(result.user);
      setFeedback({ error: false, text: result.message });
    } catch (failure) {
      setFeedback({ error: true, text: (failure as Error).message });
    } finally {
      setBusy(false);
      setPreview((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      if (input.current) input.current.value = '';
    }
  }
  function choose(file: File | undefined) {
    if (!file) return;
    if (!(AVATAR_TYPES as readonly string[]).includes(file.type)) {
      setFeedback({ error: true, text: 'Choose a JPEG, PNG or WebP picture.' });
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setFeedback({ error: true, text: 'Choose a picture smaller than 5 MB.' });
      return;
    }
    setPreview(URL.createObjectURL(file));
    const body = new FormData();
    body.append('avatar', file);
    void send('PUT', body);
  }
  const has = Boolean(user.avatarUrl);
  return (
    <div className="profile-identity profile-identity-editable">
      <label className={'profile-picture' + (busy ? ' busy' : '')}>
        <Avatar url={preview ?? user.avatarUrl} size={88} />
        <span className="profile-picture-badge" aria-hidden="true">
          <Camera size={14} />
        </span>
        <input
          ref={input}
          id={id}
          className="sr-only"
          type="file"
          accept={AVATAR_TYPES.join(',')}
          disabled={busy}
          aria-describedby={id + '-note'}
          onChange={(event) => choose(event.target.files?.[0])}
        />
        <span className="sr-only">{has ? 'Change profile picture' : 'Add a profile picture'}</span>
      </label>
      <div>
        {children}
        <div className="profile-picture-actions">
          <label htmlFor={id} aria-hidden="true">
            {busy ? 'Uploading…' : has ? 'Change picture' : 'Add a picture'}
          </label>
          {has && (
            <button type="button" disabled={busy} onClick={() => void send('DELETE')}>
              Remove picture
            </button>
          )}
        </div>
        <p id={id + '-note'} className="profile-picture-note">
          JPEG, PNG or WebP, up to 5 MB. Shown on your profile and beside your reviews.
        </p>
        {feedback && (
          <p className="profile-picture-feedback" role={feedback.error ? 'alert' : 'status'}>
            {feedback.text}
          </p>
        )}
      </div>
    </div>
  );
}
